import { useState, useRef, useEffect } from "react";
import { userDetails, UserRoleAtom } from "atoms";
import { useRecoilState } from "recoil";
import { logout } from 'components/auth/auth';
import { useNavigate } from 'react-router-dom';
import routes from 'config/routes';
import { DOC_URLS, openPdfInNewTab } from 'utils/openPdfInNewTab';
import {
    ChatBubbleOutlineOutlined,
    HelpOutlineOutlined,
    LogoutOutlined,
    KeyboardArrowDown,
    DescriptionOutlined,
    ChevronRight,
    SubdirectoryArrowRight
} from "@mui/icons-material";

export default function ProfileDropdown() {
    const [userData] = useRecoilState(userDetails);
    const [userRole] = useRecoilState(UserRoleAtom);
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isLegalExpanded, setIsLegalExpanded] = useState(false);
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const navigate = useNavigate();

    const metadata = userData?.user?.user_metadata || {};
    const firstName = metadata.first_name || '';
    const lastName = metadata.last_name || '';
    const fullNameFromMeta = metadata.full_name || '';
    const email = userData?.user?.email || '';
    const fullName = `${firstName} ${lastName}`.trim() || fullNameFromMeta.trim();

    // Generate initials for avatar: prefer first letter of user's name, fallback to email initial
    const getInitials = () => {
        if (firstName && lastName) {
            return `${firstName[0]}${lastName[0]}`.toUpperCase();
        }
        if (firstName) {
            return firstName[0].toUpperCase();
        }
        if (lastName) {
            return lastName[0].toUpperCase();
        }
        // full_name in metadata (e.g. "John Doe" -> "J" or "JD")
        if (fullNameFromMeta.trim()) {
            const parts = fullNameFromMeta.trim().split(/\s+/);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            }
            return parts[0][0].toUpperCase();
        }
        // Fallback: first letter of email (e.g. john@example.com -> "J")
        if (email) {
            const emailPrefix = email.split('@')[0] || '';
            const firstChar = emailPrefix.replace(/[^a-zA-Z]/, '').slice(0, 1) || emailPrefix.slice(0, 1);
            return firstChar ? firstChar.toUpperCase() : 'U';
        }
        return 'U';
    };

    const handleChatWithSupport = () => {
        if (window.HelpCrunch) {
            window.HelpCrunch('openChat');
            window.helpcrunchManuallyOpened = true;
    
            // Optional: listen for widget close from other sources and reset flag
            const onMessageClose = () => {
                window.helpcrunchManuallyOpened = false;
                window.removeEventListener('helpcrunch.widget-closed', onMessageClose);
            };
            window.addEventListener('helpcrunch.widget-closed', onMessageClose);
        }
        setIsOpen(false);
    };

    const handleLogout = async () => {
        const { success, error } = await logout();
        if (success) {
            localStorage.removeItem('business');
            localStorage.clear();
            navigate(routes.auth.login);
            window.location.reload();
        } else {
            console.error(error?.message);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Set fixed font sizes and border radius on menu items to prevent mobile scaling
    useEffect(() => {
        if (menuRef.current && isOpen) {
            const setStyles = () => {
                // Set font sizes
                const textSpans = menuRef.current.querySelectorAll('span.text-sm, span.text-xs');
                textSpans.forEach(span => {
                    const isSmall = span.classList.contains('text-sm');
                    span.style.setProperty('font-size', isSmall ? '14px' : '12px', 'important');
                });
                
                // Set border radius on menu items
                const menuItems = menuRef.current.querySelectorAll('button, a');
                menuItems.forEach((item, index) => {
                    const isFirst = index === 0;
                    const isLast = index === menuItems.length - 1;
                    
                    if (isFirst) {
                        item.style.setProperty('border-top-left-radius', '8px', 'important');
                        item.style.setProperty('border-top-right-radius', '8px', 'important');
                        item.style.setProperty('border-bottom-left-radius', '0', 'important');
                        item.style.setProperty('border-bottom-right-radius', '0', 'important');
                    } else if (isLast) {
                        item.style.setProperty('border-top-left-radius', '0', 'important');
                        item.style.setProperty('border-top-right-radius', '0', 'important');
                        item.style.setProperty('border-bottom-left-radius', '8px', 'important');
                        item.style.setProperty('border-bottom-right-radius', '8px', 'important');
                    } else {
                        item.style.setProperty('border-radius', '0', 'important');
                    }
                });
                
                // Set container border radius
                menuRef.current.style.setProperty('border-radius', '8px', 'important');
            };
            
            // Set immediately
            setStyles();
            
            // Also set after a short delay to catch dynamically added content
            const timeoutId = setTimeout(setStyles, 0);
            
            return () => clearTimeout(timeoutId);
        }
    }, [isOpen, isLegalExpanded]);
    
    return (
        <div className="relative" ref={dropdownRef}>
            <div
                className="flex items-center border border-gray-200 rounded-full cursor-pointer transition-colors"
                style={{
                    backgroundColor: isHovered ? '#FDFAF7' : 'transparent',
                    boxShadow: isHovered ? '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)' : 'none', // Subtle drop shadow on hover
                    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease',
                    flexShrink: 0, // Prevent button from shrinking
                    minWidth: '64px', // Fixed minimum width (avatar 40px + padding/gap ~24px)
                    width: 'fit-content', // Allow natural width but maintain minimum
                    padding: '8px', // Fixed padding - prevent shrinking
                    gap: '8px', // Fixed gap - prevent shrinking
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={() => setIsOpen(!isOpen)}
            >
                {/* Avatar with initials - dark blue 800 background */}
                <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0" 
                    style={{ 
                        backgroundColor: '#184353',
                        width: '40px',
                        height: '40px',
                        minWidth: '40px',
                        minHeight: '40px',
                        flexShrink: 0,
                        fontSize: '14px', // Fixed font size - prevent shrinking
                    }}
                >
                    {getInitials()}
                </div>
                
                {/* Dropdown Chevron */}
                <KeyboardArrowDown 
                    fontSize="small" 
                    className={`transition-all flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
                    style={{
                        color: (isOpen || isHovered) ? '#FF5F39' : '#9CA3AF',
                        // fontSize controlled by fontSize="small" prop
                        width: '20px',
                        height: '20px',
                        minWidth: '20px',
                        minHeight: '20px',
                        flexShrink: 0,
                    }}
                />
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div 
                    ref={menuRef}
                    className="profile-dropdown-menu absolute top-full right-0 mt-2 bg-white shadow-lg flex flex-col z-50 border border-gray-200"
                    style={{
                        width: '192px', // w-48 = 192px, fixed width
                        minWidth: '192px',
                        flexShrink: 0,
                        borderRadius: '8px', // rounded-lg = 8px, fixed via CSS and useEffect
                        overflow: 'hidden', // Prevent content from overflowing
                        boxSizing: 'border-box', // Include border in width calculation
                    }}
                >
                    <button
                        onClick={handleChatWithSupport}
                        className="flex items-center cursor-pointer text-left border-b border-gray-100"
                        style={{
                            padding: '12px 16px', // px-4 py-3 = 16px 12px
                            gap: '12px', // gap-3 = 12px, fixed
                            flexShrink: 0,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            borderRadius: '0', // Set via CSS and useEffect
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                            borderBottomLeftRadius: '0',
                            borderBottomRightRadius: '0',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FDFAF7';
                            // Keep border radius fixed
                            e.currentTarget.style.setProperty('border-top-left-radius', '8px', 'important');
                            e.currentTarget.style.setProperty('border-top-right-radius', '8px', 'important');
                            e.currentTarget.style.setProperty('border-bottom-left-radius', '0', 'important');
                            e.currentTarget.style.setProperty('border-bottom-right-radius', '0', 'important');
                            const icon = e.currentTarget.querySelector('svg');
                            const text = e.currentTarget.querySelector('span');
                            if (icon) icon.style.color = '#FF5F39';
                            if (text) text.style.color = '#FF5F39';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            // Restore original border radius
                            e.currentTarget.style.setProperty('border-top-left-radius', '8px', 'important');
                            e.currentTarget.style.setProperty('border-top-right-radius', '8px', 'important');
                            e.currentTarget.style.setProperty('border-bottom-left-radius', '0', 'important');
                            e.currentTarget.style.setProperty('border-bottom-right-radius', '0', 'important');
                            const icon = e.currentTarget.querySelector('svg');
                            const text = e.currentTarget.querySelector('span');
                            if (icon) icon.style.color = '#6B7280';
                            if (text) text.style.color = '#374151';
                        }}
                    >
                        <ChatBubbleOutlineOutlined 
                            fontSize="small" 
                            style={{ 
                                color: '#6B7280',
                                flexShrink: 0,
                                minWidth: '20px',
                                minHeight: '20px',
                                width: '20px',
                                height: '20px',
                            }} 
                        />
                        <span 
                            className="text-sm"
                            style={{ 
                                color: '#374151',
                                fontSize: '14px',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Chat with Support
                        </span>
                    </button>
                    <a
                        href="https://beseenhealth.crunch.help"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsOpen(false)}
                        className="flex items-center cursor-pointer text-left border-b border-gray-100"
                        style={{
                            padding: '12px 16px', // px-4 py-3 = 16px 12px
                            gap: '12px', // gap-3 = 12px, fixed
                            flexShrink: 0,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            borderRadius: '0', // Set via CSS and useEffect
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FDFAF7';
                            // Keep border radius fixed at 0
                            e.currentTarget.style.setProperty('border-radius', '0', 'important');
                            const icon = e.currentTarget.querySelector('svg');
                            const text = e.currentTarget.querySelector('span');
                            if (icon) icon.style.color = '#FF5F39';
                            if (text) text.style.color = '#FF5F39';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            // Keep border radius fixed at 0
                            e.currentTarget.style.setProperty('border-radius', '0', 'important');
                            const icon = e.currentTarget.querySelector('svg');
                            const text = e.currentTarget.querySelector('span');
                            if (icon) icon.style.color = '#6B7280';
                            if (text) text.style.color = '#374151';
                        }}
                    >
                        <HelpOutlineOutlined 
                            fontSize="small" 
                            style={{ 
                                color: '#6B7280',
                                flexShrink: 0,
                                minWidth: '20px',
                                minHeight: '20px',
                                width: '20px',
                                height: '20px',
                            }} 
                        />
                        <span 
                            className="text-sm"
                            style={{ 
                                color: '#374151',
                                fontSize: '14px',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Help Center
                        </span>
                    </a>
                    <button
                        onClick={() => setIsLegalExpanded(!isLegalExpanded)}
                        className="flex items-center justify-between cursor-pointer text-left border-b border-gray-100"
                        style={{
                            padding: '12px 16px', // px-4 py-3 = 16px 12px
                            gap: '12px', // gap-3 = 12px, fixed
                            flexShrink: 0,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            borderRadius: '0', // Set via CSS and useEffect
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FDFAF7';
                            // Keep border radius fixed at 0
                            e.currentTarget.style.setProperty('border-radius', '0', 'important');
                            const icon = e.currentTarget.querySelector('.legal-icon');
                            const text = e.currentTarget.querySelector('.legal-text');
                            const chevron = e.currentTarget.querySelector('.legal-chevron');
                            if (icon) icon.style.color = '#FF5F39';
                            if (text) text.style.color = '#FF5F39';
                            if (chevron && !isLegalExpanded) chevron.style.color = '#FF5F39';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            // Keep border radius fixed at 0
                            e.currentTarget.style.setProperty('border-radius', '0', 'important');
                            const icon = e.currentTarget.querySelector('.legal-icon');
                            const text = e.currentTarget.querySelector('.legal-text');
                            const chevron = e.currentTarget.querySelector('.legal-chevron');
                            if (icon) icon.style.color = '#6B7280';
                            if (text) text.style.color = '#374151';
                            if (chevron && !isLegalExpanded) chevron.style.color = '#9CA3AF';
                        }}
                    >
                        <div className="flex items-center" style={{ gap: '12px', flexShrink: 0 }}>
                            <DescriptionOutlined 
                                className="legal-icon" 
                                fontSize="small" 
                                style={{ 
                                    color: '#6B7280',
                                    flexShrink: 0,
                                    minWidth: '20px',
                                    minHeight: '20px',
                                    width: '20px',
                                    height: '20px',
                                }} 
                            />
                            <span 
                                className="legal-text text-sm"
                                style={{ 
                                    color: '#374151',
                                    fontSize: '14px',
                                    flexShrink: 0,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Legal
                            </span>
                        </div>
                        <KeyboardArrowDown 
                            className="legal-chevron transition-transform flex-shrink-0" 
                            fontSize="small"
                            style={{ 
                                color: isLegalExpanded ? '#FF5F39' : '#9CA3AF',
                                transform: isLegalExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                flexShrink: 0,
                                minWidth: '20px',
                                minHeight: '20px',
                                width: '20px',
                                height: '20px',
                            }}
                        />
                    </button>
                    {isLegalExpanded && (
                        <>
                            <button
                                onClick={() => {
                                    openPdfInNewTab(DOC_URLS.termsAndConditionDoc);
                                    setIsOpen(false);
                                }}
                                className="flex items-center justify-between cursor-pointer text-left border-b border-gray-100"
                                style={{
                                    padding: '8px 16px', // py-2 px-4 = 8px 16px
                                    gap: '12px', // gap-3 = 12px, fixed
                                    flexShrink: 0,
                                    width: '100%',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',
                                    borderRadius: '0', // Set via CSS and useEffect
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#FDFAF7';
                                    // Keep border radius fixed at 0
                                    e.currentTarget.style.setProperty('border-radius', '0', 'important');
                                    const indentIcon = e.currentTarget.querySelector('.indent-icon');
                                    const text = e.currentTarget.querySelector('.nested-text');
                                    const arrow = e.currentTarget.querySelector('.nested-arrow');
                                    if (indentIcon) indentIcon.style.color = '#FF5F39';
                                    if (text) text.style.color = '#FF5F39';
                                    if (arrow) arrow.style.color = '#FF5F39';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    // Keep border radius fixed at 0
                                    e.currentTarget.style.setProperty('border-radius', '0', 'important');
                                    const indentIcon = e.currentTarget.querySelector('.indent-icon');
                                    const text = e.currentTarget.querySelector('.nested-text');
                                    const arrow = e.currentTarget.querySelector('.nested-arrow');
                                    if (indentIcon) indentIcon.style.color = '#9CA3AF';
                                    if (text) text.style.color = '#374151';
                                    if (arrow) arrow.style.color = '#9CA3AF';
                                }}
                            >
                                <div className="flex items-center" style={{ gap: '12px', flexShrink: 0 }}>
                                    <SubdirectoryArrowRight 
                                        className="indent-icon flex-shrink-0" 
                                        fontSize="small" 
                                        style={{ 
                                            color: '#9CA3AF',
                                            flexShrink: 0,
                                            minWidth: '16px',
                                            minHeight: '16px',
                                            width: '16px',
                                            height: '16px',
                                        }} 
                                    />
                                    <span 
                                        className="nested-text text-xs"
                                        style={{ 
                                            color: '#374151',
                                            fontSize: '12px',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Terms & Conditions
                                    </span>
                                </div>
                                <ChevronRight 
                                    className="nested-arrow flex-shrink-0" 
                                    fontSize="small" 
                                    style={{ 
                                        color: '#9CA3AF',
                                        flexShrink: 0,
                                        minWidth: '14px',
                                        minHeight: '14px',
                                        width: '14px',
                                        height: '14px',
                                    }} 
                                />
                            </button>
                            <button
                                onClick={() => {
                                    openPdfInNewTab(DOC_URLS.privacyPolicyPayment);
                                    setIsOpen(false);
                                }}
                                className="flex items-center justify-between cursor-pointer text-left border-b border-gray-100"
                                style={{
                                    padding: '8px 16px', // py-2 px-4 = 8px 16px
                                    gap: '12px', // gap-3 = 12px, fixed
                                    flexShrink: 0,
                                    width: '100%',
                                    maxWidth: '100%',
                                    boxSizing: 'border-box',
                                    borderRadius: '0', // Set via CSS and useEffect
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#FDFAF7';
                                    // Keep border radius fixed at 0
                                    e.currentTarget.style.setProperty('border-radius', '0', 'important');
                                    const indentIcon = e.currentTarget.querySelector('.indent-icon');
                                    const text = e.currentTarget.querySelector('.nested-text');
                                    const arrow = e.currentTarget.querySelector('.nested-arrow');
                                    if (indentIcon) indentIcon.style.color = '#FF5F39';
                                    if (text) text.style.color = '#FF5F39';
                                    if (arrow) arrow.style.color = '#FF5F39';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    // Keep border radius fixed at 0
                                    e.currentTarget.style.setProperty('border-radius', '0', 'important');
                                    const indentIcon = e.currentTarget.querySelector('.indent-icon');
                                    const text = e.currentTarget.querySelector('.nested-text');
                                    const arrow = e.currentTarget.querySelector('.nested-arrow');
                                    if (indentIcon) indentIcon.style.color = '#9CA3AF';
                                    if (text) text.style.color = '#374151';
                                    if (arrow) arrow.style.color = '#9CA3AF';
                                }}
                            >
                                <div className="flex items-center" style={{ gap: '12px', flexShrink: 0 }}>
                                    <SubdirectoryArrowRight 
                                        className="indent-icon flex-shrink-0" 
                                        fontSize="small" 
                                        style={{ 
                                            color: '#9CA3AF',
                                            flexShrink: 0,
                                            minWidth: '16px',
                                            minHeight: '16px',
                                            width: '16px',
                                            height: '16px',
                                        }} 
                                    />
                                    <span 
                                        className="nested-text text-xs"
                                        style={{ 
                                            color: '#374151',
                                            fontSize: '12px',
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Privacy Policy
                                    </span>
                                </div>
                                <ChevronRight 
                                    className="nested-arrow flex-shrink-0" 
                                    fontSize="small" 
                                    style={{ 
                                        color: '#9CA3AF',
                                        flexShrink: 0,
                                        minWidth: '14px',
                                        minHeight: '14px',
                                        width: '14px',
                                        height: '14px',
                                    }} 
                                />
                            </button>
                        </>
                    )}
                    <button
                        onClick={handleLogout}
                        className="flex items-center cursor-pointer text-left"
                        style={{
                            padding: '12px 16px', // px-4 py-3 = 16px 12px
                            gap: '12px', // gap-3 = 12px, fixed
                            flexShrink: 0,
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            borderRadius: '0', // Set via CSS and useEffect
                            borderTopLeftRadius: '0',
                            borderTopRightRadius: '0',
                            borderBottomLeftRadius: '8px',
                            borderBottomRightRadius: '8px',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#FDFAF7';
                            // Keep border radius fixed
                            e.currentTarget.style.setProperty('border-top-left-radius', '0', 'important');
                            e.currentTarget.style.setProperty('border-top-right-radius', '0', 'important');
                            e.currentTarget.style.setProperty('border-bottom-left-radius', '8px', 'important');
                            e.currentTarget.style.setProperty('border-bottom-right-radius', '8px', 'important');
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            // Restore original border radius
                            e.currentTarget.style.setProperty('border-top-left-radius', '0', 'important');
                            e.currentTarget.style.setProperty('border-top-right-radius', '0', 'important');
                            e.currentTarget.style.setProperty('border-bottom-left-radius', '8px', 'important');
                            e.currentTarget.style.setProperty('border-bottom-right-radius', '8px', 'important');
                        }}
                    >
                        <LogoutOutlined 
                            fontSize="small" 
                            style={{
                                flexShrink: 0,
                                minWidth: '20px',
                                minHeight: '20px',
                                width: '20px',
                                height: '20px',
                                color: '#EF4444',
                            }}
                        />
                        <span 
                            className="text-sm"
                            style={{
                                fontSize: '14px',
                                flexShrink: 0,
                                whiteSpace: 'nowrap',
                                color: '#EF4444',
                            }}
                        >
                            Logout
                        </span>
                    </button>
                </div>
            )}
        </div>
    );
}
